/* Schema adapters: translate between the mobile backend contract and the
 * historical website shape. Keeping the mapping in ONE place lets us keep
 * existing components working while we migrate screen by screen.
 *
 * Remote (mobile backend)       → Legacy (site)
 * ─────────────────────────────────────────────────────
 * id                             → listing_id
 * agent_id                       → owner_id
 * agent.{name,phone,email,...}   → owner_name / owner_phone / owner_email
 * property_type                  → category
 * bedrooms / bathrooms / area    → chambres / salles_de_bain / superficie
 * coords.{lat,lng}               → lat / lng
 * boost_expires_at               → featured_until (+ featured derived)
 * status "publiee"               → "active"
 * status "en_attente"            → "pending"
 * status "refusee"               → "rejected"
 * status "vendue"                → "sold"
 * status "louee"                 → "rented"
 * status "expiree"               → "expired"
 */

export const STATUS_REMOTE_TO_LOCAL = {
  publiee: "active",
  en_attente: "pending",
  refusee: "rejected",
  vendue: "sold",
  louee: "rented",
  expiree: "expired",
};

export const STATUS_LOCAL_TO_REMOTE = Object.fromEntries(
  Object.entries(STATUS_REMOTE_TO_LOCAL).map(([k, v]) => [v, k])
);

export function adaptListing(r) {
  if (!r || typeof r !== "object") return r;
  const agent = r.agent || {};
  const boostUntil = r.boost_expires_at || null;
  const featured = r.featured ?? (boostUntil ? new Date(boostUntil) > new Date() : false);
  return {
    // Keep remote fields for forward-compat
    ...r,
    // Legacy aliases consumed by existing UI
    listing_id: r.id || r.listing_id,
    owner_id: r.agent_id || agent.id || r.owner_id,
    owner_name: agent.name || r.owner_name,
    owner_phone: agent.phone || r.owner_phone,
    owner_email: agent.email || r.owner_email,
    owner_picture: agent.avatar_url || agent.picture || r.owner_picture,
    agency_slug: agent.agency_slug || agent.id || r.agency_slug,
    agency_name: agent.agency_name || r.agency_name,
    category: r.property_type || r.category,
    chambres: r.bedrooms ?? r.chambres,
    salles_de_bain: r.bathrooms ?? r.salles_de_bain,
    superficie: r.area ?? r.superficie,
    lat: r.coords?.lat ?? r.lat,
    lng: r.coords?.lng ?? r.lng,
    featured,
    featured_until: boostUntil || r.featured_until,
    status: STATUS_REMOTE_TO_LOCAL[r.status] || r.status,
    // Normalize transaction_type: remote uses vente/location
    transaction_type: r.transaction_type || "vente",
    // Keep photos as-is (array of URLs)
    photos: r.photos || [],
  };
}

export function adaptUser(r) {
  if (!r || typeof r !== "object") return r;
  return {
    ...r,
    user_id: r.id || r.user_id,
    picture: r.avatar_url || r.picture,
    role: r.role || "user",
  };
}

export function adaptMessage(r) {
  if (!r || typeof r !== "object") return r;
  return {
    ...r,
    message_id: r.id || r.message_id,
    sender_id: r.sender_id,
    receiver_id: r.receiver_id,
    body: r.text ?? r.content ?? r.body,
    content: r.text ?? r.content ?? r.body,
    is_read: r.read ?? r.is_read,
    created_at: r.created_at,
  };
}

export function adaptConversation(r) {
  if (!r || typeof r !== "object") return r;
  const lm = r.last_message;
  return {
    ...r,
    conversation_id: r.id || r.conversation_id,
    other_user: r.other_user ? adaptUser(r.other_user) : null,
    listing: r.listing ? adaptListing(r.listing) : null,
    last_message:
      lm == null
        ? null
        : typeof lm === "string"
        ? { body: lm, created_at: r.last_message_at }
        : { ...lm, body: lm.text ?? lm.content ?? lm.body, created_at: lm.created_at || r.last_message_at },
    listing_id: r.listing_id || r.listing?.id || null,
    unread_count: r.unread ?? r.unread_count ?? 0,
  };
}

/** Convert remote list payload { listings:[], total, page } → unified shape. */
export function unwrapListings(data) {
  const items = (data?.listings || data?.items || []).map(adaptListing);
  return { items, total: data?.total ?? items.length };
}
